import styled from "@emotion/styled";

import { Button, FONT_WEIGHT_SEMI_BOLD, Icon, Tooltip } from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { useLayoutAncestry } from "./LayoutAncestryContext";
import { useElementStatePlainLabels } from "./shared.tsx";
import { useClearActiveContainerElementState, useContainingElementState } from "./useActiveElementState";

export const ElementStateIndicator = () => {
  const formatMessage = useFormatMessage(formatStrings);
  const elementStateLabels = useElementStatePlainLabels();

  const layout = useLayoutAncestry();
  const editingElement = useContainingElementState(layout);
  const clearActiveContainerElementState = useClearActiveContainerElementState();

  if (!layout || !editingElement) return null;

  return (
    <StickyContainer>
      <MessageWrapper>
        <IconWrapper>
          <Icon icon="exclamation-circle" />
        </IconWrapper>

        <div>
          {formatMessage(
            editingElement.nodeId !== layout.nodeId
              ? "activeElementStateIsAncestorMessage"
              : "activeElementStateMessage",
          )}
          <SelectedStateText>{elementStateLabels[editingElement.elementState]}.</SelectedStateText>
        </div>
      </MessageWrapper>

      <ButtonWrapper>
        <Tooltip title={formatMessage("clearElementStateMessage")} placement="top">
          <Button variant="outlined" size="small" onClick={() => clearActiveContainerElementState()}>
            {formatMessage("clearElementStateButton")}
          </Button>
        </Tooltip>
      </ButtonWrapper>
    </StickyContainer>
  );
};

const StickyContainer = styled.div`
  position: sticky;
  margin: 0 12px 6px;
  inset-block-start: 6px;
  z-index: 10;
  padding: 8px;

  background: ${({ theme }) => theme.primaryContainer};
  color: ${({ theme }) => theme.onPrimaryContainer};
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSize};
`;

const MessageWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconWrapper = styled.div`
  font-size: 16px;
`;

const SelectedStateText = styled.span`
  text-transform: capitalize;
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
  margin-left: 4px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 8px;
`;

const formatStrings = {
  activeElementStateIsAncestorMessage: {
    en: `Currently overwriting styles for when a parent element state is:`,
  },
  activeElementStateMessage: {
    en: `Currently overwriting styles for when element is:`,
  },
  clearElementStateMessage: {
    en: "If you wish to edit styles for the default state, switch to default.",
  },
  clearElementStateButton: {
    en: "Switch to Default",
  },
};
